import * as THREE from 'three';
import { dropPickupsBefore } from '@/physics/pickups';
import type { Track } from '@/world/Track';
import { shortestAngleDelta } from '@/math/angles';
import { tuning, type DifficultyLevel } from '@/config/tuning';
import type { CourseExtras } from '@/config/cities';
import type { InputFrame } from '@/input/InputManager';
import { SimCore, type RacerInput } from '@/sim/SimCore';
import type { SimRacer } from '@/sim/SimRacer';
import type { RaceFx, RaceLike } from '@/core/RaceLike';
import { CarView, ballOnNose } from '@/entities/CarView';
import { getPlayerLook, getPlayerLookKey, getPlayerLookVehicle } from '@/entities/carLook';
import { VEHICLES, getPlayerVehicle, stockColorOf } from '@/entities/vehicles';
import { BallView } from '@/entities/BallView';
import { PickupsView } from '@/entities/PickupsView';
import { TackleGhost } from '@/entities/TackleGhost';
import { FreeKickWallView } from '@/entities/FreeKickWallView';
import { MudView } from '@/entities/MudView';
import { MoversView } from '@/entities/MoversView';
import { isActive } from '@/physics/powerups';
import {
  sfxBallHit,
  sfxBoost,
  sfxCollect,
  sfxCountdown,
  sfxDeny,
  sfxDozerHit,
  sfxDozerTransform,
  sfxItemLand,
  sfxKick,
  sfxMagnet,
  sfxOffside,
  sfxPibe,
  sfxRouletteSpin,
  sfxMud,
  sfxScorpion,
  sfxShield,
  sfxShieldBlock,
  sfxSpinOut,
  sfxShortcut,
  sfxStuds,
  sfxStudsHit,
  sfxTackleHit,
  sfxTackleLaunch,
  sfxWallBreak,
  sfxWallPlant,
} from '@/audio/sfx';

export type { RaceState } from '@/sim/SimCore';
export type { RaceFx } from '@/core/RaceLike';

/**
 * Race.ts — the CLIENT shell around the headless {@link SimCore}: it owns the
 * three.js views (one CarView + BallView per racer, the floating item boxes, the
 * slide-tackle ghost), feeds the local player's input into the sim each tick, and
 * drains the sim's `events` into audio + the player's screen-shake/toast hooks.
 *
 * The simulation itself — countdown, every collision, the item engine, finish +
 * standings — lives in SimCore (pure, three.js-free, server-runnable). This file
 * is deliberately the ONLY place three.js touches a race, so the same SimCore can
 * run authoritatively online (M18 headless split). Game.ts and RaceRecorder.ts
 * still see the exact same public surface they always did (player / racers /
 * standings / state / raceTime / update / render / reset / loadTrack / dispose).
 */
export class Race implements RaceLike {
  fx: RaceFx = {};

  private sim: SimCore;
  private track: Track;
  private views = new Map<
    number,
    { car: CarView; ball: BallView; vehicleId: string; lookKey: string }
  >();
  private pickupsView = new PickupsView();
  private tackleGhost = new TackleGhost();
  private wallsView = new FreeKickWallView();
  private mudView = new MudView();
  private moversView = new MoversView();
  /** Whose thumps/toasts are "mine" (offline: the local player). */
  private localPlayerId: number;
  /** True while a tackle ghost is shown — so we hide it on the falling edge. */
  private ghostShown = false;

  constructor(
    private scene: THREE.Scene,
    track: Track,
    difficulty: DifficultyLevel,
    course: CourseExtras | undefined,
    seed: number,
    fieldSize: number,
  ) {
    this.sim = new SimCore(track, difficulty, course, seed, fieldSize);
    // M49: offline, the player's chosen vehicle brings its stat profile.
    this.sim.setPlayerVehicle(getPlayerVehicle());
    this.track = track;
    scene.add(this.pickupsView.group);
    scene.add(this.tackleGhost.group);
    scene.add(this.wallsView.group);
    scene.add(this.mudView.group);
    scene.add(this.moversView.group);
    this.pickupsView.setSpots(this.sim.pickups);
    this.localPlayerId = this.sim.player.id;
    this.syncViews();
    this.warpViews();
  }

  // --- public surface preserved for Game.ts / RaceRecorder.ts ---
  get racers(): SimRacer[] {
    return this.sim.racers;
  }
  get player(): SimRacer {
    return this.sim.player;
  }
  get state(): SimCore['state'] {
    return this.sim.state;
  }
  get countdown(): number {
    return this.sim.countdown;
  }
  get goFlash(): number {
    return this.sim.goFlash;
  }
  get raceTime(): number {
    return this.sim.raceTime;
  }
  standings(): SimRacer[] {
    return this.sim.standings();
  }
  playerPlace(): number {
    return this.sim.playerPlace();
  }
  /** M112: the onboarding demo race drives the sim directly (countdown skip,
   *  the pinned BOOST roll) — the offline mirror of NetRace.predictionSim. */
  get simCore(): SimCore {
    return this.sim;
  }

  /** Demo race only (Flow.runDemoRace): drop the ? box rows closer than
   *  `minDistance` to the start so the first collect comes after a proper
   *  dribble. Sim truth AND the rendered boxes move together. */
  trimPickupsBefore(minDistance: number): void {
    this.sim.pickups = dropPickupsBefore(this.sim.pickups, minDistance);
    this.pickupsView.setSpots(this.sim.pickups);
  }

  loadTrack(
    track: Track,
    difficulty: DifficultyLevel,
    course: CourseExtras | undefined,
    seed: number,
    fieldSize: number,
  ): void {
    this.sim.loadTrack(track, difficulty, course, seed, fieldSize);
    // A field-size change rebuilds the racers (fresh roster defaults) — the
    // player's pick must survive it (M51 fix: found in self-review).
    this.sim.setPlayerVehicle(getPlayerVehicle());
    this.track = track;
    this.pickupsView.setSpots(this.sim.pickups);
    this.syncViews(); // racers may have been rebuilt (field-size change)
    this.localPlayerId = this.sim.player.id;
    this.tackleGhost.hide();
    this.wallsView.hide();
    this.mudView.hide();
    this.moversView.hide();
    this.ghostShown = false;
    this.warpViews();
  }

  reset(): void {
    this.sim.reset();
    this.tackleGhost.hide();
    this.wallsView.hide();
    this.mudView.hide();
    this.ghostShown = false;
    this.warpViews();
  }

  update(dt: number, input: InputFrame): void {
    // Offline: the only human is the local player. §6.2 — flip the screen-space
    // steer into world space HERE (the sim is world-space for everyone).
    const inputs = new Map<number, RacerInput>([
      [
        this.localPlayerId,
        { throttle: input.throttle, steer: -input.steer, useItem: input.useItem },
      ],
    ]);
    this.sim.step(inputs, dt);
    this.drainEvents();
  }

  /** Turn the sim's emitted events into sound + the player's feel hooks. The
   *  "for everyone" sounds (an item fired anywhere) always play; the personal
   *  ones (your thump, your toast) only when the event is the local player's. */
  private drainEvents(): void {
    const me = this.localPlayerId;
    // One tyre screech per drain, however many victims spun this tick (an
    // offside trap can spin the whole field at once — stacked identical
    // squeals would just clip). The loudest requested variant wins by order.
    let screeched = false;
    const spinScreech = (hard: boolean): void => {
      if (screeched) return;
      screeched = true;
      sfxSpinOut(hard);
    };
    for (const e of this.sim.events) {
      switch (e.type) {
        case 'countdown':
          sfxCountdown(e.go);
          break;
        case 'pad':
          if (e.racerId === me) sfxBoost(); // M99: your pad hit = the boost whoosh
          break;
        case 'itemLand':
          if (e.racerId === me) sfxItemLand();
          break;
        case 'collect':
          if (e.racerId === me) {
            sfxCollect();
            sfxRouletteSpin(tuning.powerups.rouletteTime);
          }
          break;
        case 'fire':
          switch (e.kind) {
            case 'boost':
              sfxBoost();
              break;
            case 'magnet':
              sfxMagnet();
              break;
            case 'dozer':
              sfxDozerTransform(); // the car visibly becomes a bulldozer (CarView)
              break;
            case 'shield':
              sfxShield();
              break;
            case 'kick':
              sfxKick();
              if (e.racerId === me) this.fx.onPlayerBallHit?.(0.7);
              this.popKick(e.racerId); // M145: impact burst at the struck ball
              break;
            case 'wall':
              sfxWallPlant();
              break;
            case 'offside':
              sfxOffside();
              this.views.get(e.racerId)?.car.triggerOffside(); // M145: the flag goes up
              break;
            case 'pibe':
              sfxPibe();
              if (e.racerId !== me) this.fx.onPibeLoose?.();
              break;
            case 'studs':
              sfxStuds();
              if (e.racerId !== me) this.fx.onStudsLoose?.();
              break;
            case 'scorpion':
              sfxScorpion();
              break;
            case 'mud':
              sfxMud();
              this.views.get(e.racerId)?.car.triggerMud(); // spray out the firer's back
              break;
          }
          break;
        case 'deny':
          if (e.racerId === me) {
            sfxDeny();
            this.fx.onPlayerDenied?.();
          }
          break;
        case 'ballHit':
          if (e.racerId === me) {
            sfxBallHit(e.intensity);
            this.fx.onPlayerBallHit?.(e.intensity);
          }
          break;
        case 'dozerHit':
          sfxDozerHit();
          spinScreech(true); // M58: the victim's tyres squeal into the spin
          if (e.victimId === me) this.fx.onPlayerDozed?.();
          this.launchDozerTumble(e.victimId);
          break;
        case 'shieldBlock':
          sfxShieldBlock();
          this.flashShields(); // M145: the bubble that took the hit flares white
          break;
        case 'tackleLaunch':
          this.tackleGhost.launch(e.color);
          this.ghostShown = true;
          sfxTackleLaunch();
          if (e.targetId === me) this.fx.onPlayerTackleIncoming?.();
          break;
        case 'tackleHit':
          sfxTackleHit();
          spinScreech(true);
          if (e.victimId === me) this.fx.onPlayerDozed?.();
          break;
        case 'offsideHit':
          spinScreech(false); // M58: the caught pack wobbles with a soft squeal
          if (e.victimId === me) this.fx.onPlayerOffside?.();
          break;
        case 'pibeHit':
          sfxDozerHit(); // same body-check thud — the golden runner plows through
          spinScreech(true);
          if (e.victimId === me) this.fx.onPlayerDozed?.();
          break;
        case 'clash':
          sfxDozerHit(); // M57: a weight-class hit landed (truck swats bike)
          spinScreech(e.tier === 'spin'); // M58: squeal scaled to the tier
          if (e.victimId === me && e.tier === 'spin') this.fx.onPlayerDozed?.();
          break;
        case 'studsHit':
          sfxStudsHit(); // M59: a rival shredded on the spikes
          spinScreech(true);
          if (e.victimId === me) this.fx.onPlayerDozed?.();
          break;
        case 'wallBreak':
          sfxWallBreak();
          break;
        case 'shortcut':
          sfxShortcut(); // M116: everyone hears a racer vanish into a secret cut
          if (e.racerId === me) this.fx.onPlayerShortcut?.();
          break;
        case 'finish':
          break; // offline: Game builds results from standings()
      }
    }
  }

  /** M145: the KICK's impact burst, at the kicker's ball. */
  private popKick(racerId: number): void {
    const r = this.sim.racers.find((x) => x.id === racerId);
    const b = r?.ball.state;
    if (b) this.views.get(racerId)?.car.triggerKick(b.x, b.y, b.z);
  }

  /** M145: a shield turned a hit away. The event carries no racer, so every
   *  bubble that is up flares — only the one that blocked is ever in a fight. */
  private flashShields(): void {
    for (const r of this.sim.racers) {
      if (isActive(r.powerups, 'shield')) this.views.get(r.id)?.car.triggerShieldBlock();
    }
  }

  /** Draw all racers + the item boxes + the tackle ghost, interpolated. */
  render(alpha: number, frameDt: number): void {
    for (const r of this.sim.racers) {
      let v = this.views.get(r.id);
      if (!v) continue;
      // M51: the SIM owns each racer's vehicle now — rebuild a view whose
      // model no longer matches (car-select change, online roster updates).
      // M87: ...or whose equipped LOOK changed (the attract world builds views
      // before the save loads; a shop equip must reach the next race too).
      const wantLook = this.playerLookApplies(r) ? getPlayerLookKey() : '';
      if (v.vehicleId !== r.vehicleId || (!r.isBot && v.lookKey !== wantLook)) {
        v = this.rebuildView(r, v);
      }
      v.car.setBoosting(isActive(r.powerups, 'boost') || r.padBoost > 0); // M99: pads flame too
      v.car.setDozer(isActive(r.powerups, 'dozer'));
      v.car.setShield(isActive(r.powerups, 'shield'));
      v.car.setPibe(isActive(r.powerups, 'pibe'));
      v.car.setStunned(r.stunTimer > 0); // M58: dizzy stars + smoke + wobble
      v.car.setStuds(isActive(r.powerups, 'studs')); // M59: spinning spikes
      v.car.setScorpion(r.scorpionTimer); // M60b: two-phase pop-up → flip volley
      const cs = r.car.snapshot(alpha);
      const bs = r.ball.snapshot(alpha);
      v.car.setMagnet(isActive(r.powerups, 'magnet'), bs.x, bs.y, bs.z); // M145: the field
      v.car.setDribbling(ballOnNose(cs, bs, r.vehicleId)); // M128: the gloves' grab
      v.car.update(cs, frameDt);
      v.ball.update(bs, frameDt);
    }
    this.pickupsView.update(frameDt, this.sim.pickups);
    this.wallsView.update(this.sim.walls);
    this.mudView.update(this.sim.mud);
    this.moversView.update(this.track, this.sim.movers, this.sim.raceTime);
    const t = this.sim.tackle;
    if (t) {
      const s = this.track.sampleAt(t.dist);
      const slide01 = t.phase === 'slide' ? t.timer / tuning.powerups.tackle.slideTime : 0;
      this.tackleGhost.update(
        s.x + s.rightX * t.lat,
        s.y + 0.15,
        s.z + s.rightZ * t.lat,
        t.heading,
        slide01,
      );
    } else if (this.ghostShown) {
      this.tackleGhost.hide();
      this.ghostShown = false;
    }
  }

  /** Make the view set match the sim's racers (add new, drop gone). */
  private syncViews(): void {
    for (const [id, v] of this.views) {
      if (!this.sim.racers.some((r) => r.id === id)) {
        this.scene.remove(v.car.group);
        this.scene.remove(v.ball.mesh);
        v.car.dispose();
        this.views.delete(id);
      }
    }
    for (const r of this.sim.racers) {
      if (this.views.has(r.id)) continue;
      this.views.set(r.id, this.makeView(r));
    }
  }

  /** Build one racer's views from ITS vehicle (M51 — the sim is the truth).
   *  M79: the human's car wears their equipped shop cosmetics (display only;
   *  online fields sync looks through the room — see NetRace, M81). */
  private makeView(r: SimRacer): {
    car: CarView;
    ball: BallView;
    vehicleId: string;
    lookKey: string;
  } {
    const dressed = this.playerLookApplies(r);
    const look = dressed ? getPlayerLook() : undefined;
    // M93 (owner rule): an undressed car wears its VEHICLE's stock livery —
    // the same paint the garage shows — not the racer's ball colour. The BALL
    // keeps the slot colour: it alone is your on-course identity.
    const car = new CarView(
      stockColorOf(r.vehicleId),
      VEHICLES[r.vehicleId] ?? VEHICLES.sports,
      look,
    );
    const ball = new BallView(r.color, tuning.ball.radius);
    this.scene.add(car.group);
    this.scene.add(ball.mesh);
    return { car, ball, vehicleId: r.vehicleId, lookKey: dressed ? getPlayerLookKey() : '' };
  }

  /**
   * M109 — does the published player look belong on THIS car?
   *
   * Loadouts are per-vehicle, so the seam is only valid for the ride it was
   * published for. That used to be implicit: the seam and the sim's car always
   * changed together through applyVehicle. The account layer can now adopt a
   * DIFFERENT ride mid-session (a garage synced from another device), so the
   * two can briefly disagree — and painting a sports car's wrap onto the
   * muscle car currently on track is worse than leaving it stock.
   */
  private playerLookApplies(r: SimRacer): boolean {
    if (r.isBot) return false;
    const forVehicle = getPlayerLookVehicle();
    return forVehicle === '' || forVehicle === r.vehicleId;
  }

  /** Swap a racer's view for its (changed) vehicle or look, in place. */
  private rebuildView(
    r: SimRacer,
    old: { car: CarView; ball: BallView },
  ): { car: CarView; ball: BallView; vehicleId: string; lookKey: string } {
    this.scene.remove(old.car.group);
    this.scene.remove(old.ball.mesh);
    old.car.dispose(); // M128: the old mods' GPU resources
    const v = this.makeView(r);
    this.views.set(r.id, v);
    v.car.warp(r.car.snapshot(1));
    v.ball.warp(r.ball.snapshot(1));
    return v;
  }

  private warpViews(): void {
    for (const r of this.sim.racers) {
      const v = this.views.get(r.id);
      if (!v) continue;
      v.car.warp(r.car.snapshot(1));
      v.ball.warp(r.ball.snapshot(1));
    }
  }

  /** The player picked a new vehicle (M35 car-select). Setting it on the SIM
   *  is enough now (M51): the render loop sees the changed vehicleId and
   *  rebuilds the view (and the stats follow, M49). */
  rebuildPlayerView(): void {
    this.sim.setPlayerVehicle(getPlayerVehicle());
  }

  dispose(): void {
    for (const v of this.views.values()) {
      this.scene.remove(v.car.group);
      this.scene.remove(v.ball.mesh);
    }
    this.views.clear();
    this.scene.remove(this.pickupsView.group);
    this.pickupsView.dispose();
    this.scene.remove(this.tackleGhost.group);
    this.tackleGhost.dispose();
    this.scene.remove(this.wallsView.group);
    this.wallsView.dispose();
    this.scene.remove(this.mudView.group);
    this.mudView.dispose();
    this.scene.remove(this.moversView.group);
    this.moversView.dispose();
  }

  /** M115 (owner) — a bulldozer landed a hit: throw the victim into two flips
   *  and land it square with the road. Presentation only, and triggered by the
   *  'dozerHit' EVENT (which every online client already receives), so this
   *  needs no new synced state and no protocol bump. */
  private launchDozerTumble(victimId: number): void {
    const view = this.views.get(victimId)?.car;
    const r = this.sim.racers.find((x) => x.id === victimId);
    if (!view || !r) return;
    const st = r.car.state;
    const dir = this.track.sampleAt(this.track.project(st.x, st.z).distance).dir;
    view.triggerTumble(tuning.powerups.dozerTumble, shortestAngleDelta(st.heading, dir));
  }
}
